import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Card,
  Spin,
  Alert,
  Typography,
  Space,
  Button,
  Tag,
  Statistic,
  Row,
  Col,
  DatePicker,
  Select,
  Collapse,
  message,
  Tooltip,
} from "antd";
import {
  StarOutlined,
  StarFilled,
  FileExcelOutlined,
  SwapOutlined,
} from "@ant-design/icons";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import {
  getUpcomingInvoices,
  setUpcomingPaymentPriority,
} from "../../api/accounting";
import dayjs from "dayjs";
import { convertToUSD } from "../../utils/currencyUtils";
import {
  getCachedData,
  setCachedData,
  CacheTTL,
} from "../../context/AccountingDataCache";
import useDisplayCurrency from "../../../../hooks/useDisplayCurrency";
import "./UpcomingPayments.css";
import { exportUpcomingVendorExcel } from "../../utils/exportUpcomingVendorExcel";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const UpcomingBills = ({ firstBrand = null, selectedBrands = [] }) => {
  const {
    refreshKey: currencyRefreshKey,
    displayCurrency,
    exchangeRate,
    isConverted,
  } = useDisplayCurrency();

  const [error, setError] = useState(null);
  const [priorityItems, setPriorityItems] = useState(new Set());
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, "month").startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [activeKeys, setActiveKeys] = useState([]);

  const fetchingRef = useRef(false);
  const lastFetchedCacheKeyRef = useRef(null);

  const brandsToUse = useMemo(() => {
    if (selectedBrands && selectedBrands.length > 0) return selectedBrands;
    return firstBrand ? [firstBrand] : [];
  }, [selectedBrands, firstBrand]);

  const brandsKey = useMemo(() => {
    if (!brandsToUse || brandsToUse.length === 0) return "";
    return brandsToUse.map((b) => b.id).sort().join(",");
  }, [brandsToUse]);

  const dateRangeKey = useMemo(() => {
    if (!dateRange || dateRange.length !== 2) return "";
    return `${dateRange[0]?.format("YYYY-MM-DD") || ""}_${dateRange[1]?.format("YYYY-MM-DD") || ""}`;
  }, [dateRange]);

  const cacheKey = useMemo(() => {
    if (!brandsKey) return null;
    return `upcoming-bills-${brandsKey}-${dateRangeKey}`;
  }, [brandsKey, dateRangeKey]);

  const initialBillsData = useMemo(() => {
    if (!cacheKey) return [];
    return getCachedData(cacheKey) || [];
  }, [cacheKey]);

  const [loading, setLoading] = useState(initialBillsData.length === 0);
  const [billsData, setBillsData] = useState(initialBillsData);
  const [selectedVendors, setSelectedVendors] = useState([]);

  const extractInvoiceId = useCallback((compositeId) => {
    const idStr = String(compositeId);
    if (idStr.includes("_")) return parseInt(idStr.split("_")[0]);
    return parseInt(compositeId);
  }, []);

  const handlePriorityToggle = useCallback(
    async (rowData) => {
      const compositeId = String(rowData.id);
      const currentPriority = priorityItems.has(compositeId);
      const newPriority = !currentPriority;

      setPriorityItems((prev) => {
        const newSet = new Set(prev);
        newPriority ? newSet.add(compositeId) : newSet.delete(compositeId);
        return newSet;
      });

      const updatedData = billsData.map((bill) =>
        String(bill.id) === compositeId ? { ...bill, is_priority: newPriority } : bill
      );
      setBillsData(updatedData);

      try {
        const brandId = rowData.brand_id || brandsToUse[0]?.id;
        const realInvoiceId = extractInvoiceId(compositeId);
        if (brandId && realInvoiceId) {
          await setUpcomingPaymentPriority(realInvoiceId, brandId, newPriority, compositeId);
        }
      } catch {
        setPriorityItems((prev) => {
          const newSet = new Set(prev);
          newPriority ? newSet.delete(compositeId) : newSet.add(compositeId);
          return newSet;
        });
        const revertedData = billsData.map((bill) =>
          String(bill.id) === compositeId ? { ...bill, is_priority: !newPriority } : bill
        );
        setBillsData(revertedData);
      }
    },
    [priorityItems, billsData, brandsToUse, extractInvoiceId]
  );

  const filteredBillsData = useMemo(() => {
    if (!selectedVendors || selectedVendors.length === 0) return billsData;
    return billsData.filter((b) => selectedVendors.includes(b.vendor_name));
  }, [billsData, selectedVendors]);

  const handleVendorPriorityToggle = useCallback(
    async (vendorName) => {
      const vendorBills = filteredBillsData.filter((b) => (b.vendor_name || "Unknown") === vendorName);
      if (vendorBills.length === 0) return;

      const allArePriority = vendorBills.every((b) => priorityItems.has(String(b.id)) || b.is_priority);
      const newPriority = !allArePriority;

      const ids = vendorBills.map((b) => String(b.id));
      setPriorityItems((prev) => {
        const newSet = new Set(prev);
        ids.forEach((id) => (newPriority ? newSet.add(id) : newSet.delete(id)));
        return newSet;
      });
      setBillsData((prev) =>
        prev.map((bill) => (ids.includes(String(bill.id)) ? { ...bill, is_priority: newPriority } : bill))
      );

      const failed = [];
      for (const bill of vendorBills) {
        try {
          const brandId = bill.brand_id || brandsToUse[0]?.id;
          const realId = extractInvoiceId(String(bill.id));
          if (brandId && realId) {
            await setUpcomingPaymentPriority(realId, brandId, newPriority, String(bill.id));
          }
        } catch {
          failed.push(String(bill.id));
        }
      }
      if (failed.length > 0) {
        message.warning(`${failed.length} item(s) failed to update priority`);
        setPriorityItems((prev) => {
          const newSet = new Set(prev);
          failed.forEach((id) => (newPriority ? newSet.delete(id) : newSet.add(id)));
          return newSet;
        });
        setBillsData((prev) =>
          prev.map((bill) => (failed.includes(String(bill.id)) ? { ...bill, is_priority: !newPriority } : bill))
        );
      }
    },
    [filteredBillsData, priorityItems, brandsToUse, extractInvoiceId]
  );

  const fetchUpcomingBills = useCallback(
    async (force = false) => {
      if (!brandsToUse || brandsToUse.length === 0 || !cacheKey) {
        setError("Please select a brand");
        setBillsData([]);
        return;
      }

      if (!force) {
        const cachedData = getCachedData(cacheKey);
        if (cachedData) {
          setBillsData(cachedData);
          const priorityIds = new Set(cachedData.filter((b) => b.is_priority).map((b) => String(b.id)));
          setPriorityItems(priorityIds);
          setLoading(false);
          lastFetchedCacheKeyRef.current = cacheKey;
          return;
        }
      }

      if (!force && billsData.length > 0 && lastFetchedCacheKeyRef.current === cacheKey) {
        setLoading(false);
        return;
      }

      if (fetchingRef.current && !force) return;
      fetchingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const brands = brandsToUse.map((b) => b.id);
        const startDate = dateRange?.[0]?.startOf("day") || dayjs().subtract(1, "month").startOf("month");
        const endDate = dateRange?.[1]?.startOf("day") || dayjs().endOf("month");

        const data = await getUpcomingInvoices(brands, {
          upcoming_only: true,
          invoice_type: "bill",
          start_date: startDate.format("YYYY-MM-DD"),
          end_date: endDate.format("YYYY-MM-DD"),
        });

        if (!data || !data.upcoming_invoices) {
          setBillsData([]);
          setPriorityItems(new Set());
          return;
        }

        const bills = data.upcoming_invoices || [];
        const priorityIds = new Set(bills.filter((b) => b.is_priority).map((b) => String(b.id)));
        setPriorityItems(priorityIds);
        setBillsData(bills);
        setCachedData(cacheKey, bills, CacheTTL.MEDIUM);
        lastFetchedCacheKeyRef.current = cacheKey;
      } catch (err) {
        setError(`Failed to load upcoming bills: ${err.response?.data?.error || err.message}`);
        setBillsData([]);
        setPriorityItems(new Set());
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    },
    [brandsToUse, dateRange, cacheKey, billsData.length]
  );

  useEffect(() => {
    if (!brandsKey || !cacheKey) {
      setBillsData([]);
      setPriorityItems(new Set());
      return;
    }
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      setBillsData(cachedData);
      const priorityIds = new Set(cachedData.filter((b) => b.is_priority).map((b) => String(b.id)));
      setPriorityItems(priorityIds);
      setLoading(false);
      lastFetchedCacheKeyRef.current = cacheKey;
      return;
    }
    if (billsData.length === 0 || lastFetchedCacheKeyRef.current !== cacheKey) {
      fetchUpcomingBills();
    }
  }, [cacheKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const vendorOptions = useMemo(() => {
    const vendors = [...new Set(billsData.map((b) => b.vendor_name).filter(Boolean))].sort();
    return vendors.map((v) => ({ label: v, value: v }));
  }, [billsData]);

  const vendorGroups = useMemo(() => {
    const groups = {};
    filteredBillsData.forEach((bill) => {
      const vName = bill.vendor_name || "Unknown";
      if (!groups[vName]) {
        groups[vName] = { vendor_name: vName, bills: [], totalAmount: 0 };
      }
      groups[vName].bills.push(bill);
      const converted = convertToUSD(bill.amount || 0, bill.currency || "$");
      groups[vName].totalAmount += converted;
    });
    return Object.values(groups).sort((a, b) => a.vendor_name.localeCompare(b.vendor_name));
  }, [filteredBillsData]);

  useEffect(() => {
    setActiveKeys(vendorGroups.map((g) => g.vendor_name));
  }, [vendorGroups]);

  const totals = useMemo(() => {
    const count = filteredBillsData.length;
    const totalAmount = filteredBillsData.reduce((sum, bill) => {
      return sum + convertToUSD(bill.amount || 0, bill.currency || "$");
    }, 0);
    return { count, totalAmount };
  }, [filteredBillsData]);

  const fmtAmount = useCallback(
    (val) => {
      const display = isConverted ? val * exchangeRate : val;
      return display.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    [isConverted, exchangeRate]
  );

  const currencyPrefix = isConverted ? displayCurrency : "$";

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressMovable: false,
    }),
    []
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Priority",
        field: "is_priority",
        width: 90,
        maxWidth: 90,
        sortable: true,
        filter: false,
        cellRenderer: (params) => {
          const record = params.data;
          if (!record) return null;

          if (record._isTotal) {
            const vendorName = String(record.id).replace("total-", "");
            const vendorBills = filteredBillsData.filter((b) => (b.vendor_name || "Unknown") === vendorName);
            const allPriority = vendorBills.length > 0 && vendorBills.every((b) => priorityItems.has(String(b.id)) || b.is_priority);
            return (
              <Tooltip title={allPriority ? "Remove priority from all" : "Mark all as priority"}>
                <Button
                  type={allPriority ? "primary" : "default"}
                  size="small"
                  icon={allPriority ? <StarFilled /> : <StarOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVendorPriorityToggle(vendorName);
                  }}
                  style={{
                    backgroundColor: allPriority ? "#faad14" : undefined,
                    borderColor: allPriority ? "#faad14" : undefined,
                  }}
                />
              </Tooltip>
            );
          }

          const compositeId = String(record.id);
          const isPriority = priorityItems.has(compositeId) || record.is_priority;
          return (
            <Button
              type={isPriority ? "primary" : "default"}
              size="small"
              icon={isPriority ? <StarFilled /> : <StarOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handlePriorityToggle(record);
              }}
              style={{
                backgroundColor: isPriority ? "#faad14" : undefined,
                borderColor: isPriority ? "#faad14" : undefined,
              }}
            />
          );
        },
      },
      {
        headerName: "Client",
        field: "client_name",
        minWidth: 150,
        flex: 1,
      },
      {
        headerName: "Brand",
        field: "brand_name",
        minWidth: 120,
        flex: 1,
      },
      {
        headerName: "Invoice Date",
        field: "invoice_date",
        width: 120,
        valueFormatter: (params) =>
          params.value ? dayjs(params.value).format("DD-MM-YYYY") : "-",
      },
      {
        headerName: "Bill Number",
        field: "invoice_number",
        width: 120,
      },
      {
        headerName: "Payment Terms",
        field: "payment_terms_text",
        minWidth: 160,
        flex: 1,
        valueGetter: (params) =>
          params.data?.payment_terms_text || params.data?.description || "-",
      },
      {
        headerName: "Percentage",
        field: "percentage",
        width: 110,
        type: "numericColumn",
      },
      {
        headerName: "Due Date",
        field: "due_date",
        width: 120,
        valueFormatter: (params) =>
          params.value ? dayjs(params.value).format("DD-MM-YYYY") : "-",
      },
      {
        headerName: "Due In",
        field: "due_in_text",
        width: 140,
        cellRenderer: (params) => {
          const record = params.data;
          if (!record) return "-";
          if (record.is_overdue) {
            return <Tag color="red">{params.value || "OVERDUE"}</Tag>;
          }
          return <span style={{ color: "#52c41a" }}>{params.value || "-"}</span>;
        },
      },
      {
        headerName: "Total Amount Due",
        field: "amount",
        width: 160,
        type: "numericColumn",
        valueGetter: (params) => {
          if (!params.data) return 0;
          return convertToUSD(params.data.amount || 0, params.data.currency || "$");
        },
        valueFormatter: (params) => `${currencyPrefix} ${fmtAmount(params.value || 0)}`,
        cellStyle: (params) => {
          if (params.data?.is_overdue) return { color: "#ff4d4f", fontWeight: 600 };
          return { fontWeight: 500 };
        },
      },
    ],
    [priorityItems, handlePriorityToggle, handleVendorPriorityToggle, filteredBillsData, currencyPrefix, fmtAmount]
  );

  const getRowStyle = useCallback(
    (params) => {
      if (!params.data) return {};
      const isPriority = priorityItems.has(String(params.data.id)) || params.data.is_priority;
      if (isPriority && params.data.is_overdue) return { background: "#ffa39e" };
      if (params.data.is_overdue) return { background: "#fff2f0" };
      if (isPriority) return { background: "#e6f7ff" };
      return {};
    },
    [priorityItems]
  );

  const billExportHeaders = [
    "Vendor",
    "Client",
    "Brand",
    "Invoice Date",
    "Bill Number",
    "Payment Terms",
    "Percentage",
    "Due Date",
    "Due In",
    "Total Amount Due (USD)",
  ];

  const mapBillToExportCells = (bill) => [
    bill.vendor_name,
    bill.client_name || "",
    bill.brand_name || "",
    bill.invoice_date ? dayjs(bill.invoice_date).format("DD-MM-YYYY") : "",
    bill.invoice_number,
    bill.payment_terms_text || bill.description || "",
    bill.percentage ?? "",
    bill.due_date ? dayjs(bill.due_date).format("DD-MM-YYYY") : "",
    bill.due_in_text || "",
    Number(convertToUSD(bill.amount || 0, bill.currency || "$").toFixed(2)),
  ];

  const handleExportCSV = () => {
    if (filteredBillsData.length === 0) {
      message.warning("No data to export");
      return;
    }

    const dateLabel = dateRange ? `${dateRange[0]?.format("YYYY-MM-DD")} to ${dateRange[1]?.format("YYYY-MM-DD")}` : "";
    const vendorLabel = selectedVendors.length > 0 ? selectedVendors.join(", ") : "All Vendors";
    const rows = [];

    vendorGroups.forEach((group) => {
      rows.push([`=== Vendor: ${group.vendor_name} ===`, "", "", "", "", "", "", "", "", ""]);
      rows.push(billExportHeaders);
      group.bills.forEach((bill) => {
        rows.push([
          bill.vendor_name,
          bill.client_name || "",
          bill.brand_name || "",
          bill.invoice_date ? dayjs(bill.invoice_date).format("DD-MM-YYYY") : "",
          bill.invoice_number,
          bill.payment_terms_text || bill.description || "",
          bill.percentage ?? "",
          bill.due_date ? dayjs(bill.due_date).format("DD-MM-YYYY") : "",
          bill.due_in_text || "",
          convertToUSD(bill.amount || 0, bill.currency || "$").toFixed(2),
        ]);
      });
      rows.push(["Total", "", "", "", "", "", "", "", "", group.totalAmount.toFixed(2)]);
      rows.push([]);
    });

    const csvContent = [
      `Payment Schedule - Bills (by vendor),,,,,,,,,`,
      `Date Range: ${dateLabel},,,,,,,,,`,
      `Vendors: ${vendorLabel},,,,,,,,,`,
      ``,
      ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Upcoming_Bills_${dayjs().format("YYYY-MM-DD")}.csv`;
    link.click();
    message.success("Exported successfully!");
  };

  const handleExportExcel = async () => {
    if (filteredBillsData.length === 0) {
      message.warning("No data to export");
      return;
    }
    const dateLabel = dateRange
      ? `${dateRange[0]?.format("YYYY-MM-DD")} to ${dateRange[1]?.format("YYYY-MM-DD")}`
      : "";
    const vendorLabel = selectedVendors.length > 0 ? selectedVendors.join(", ") : "All Vendors";
    try {
      await exportUpcomingVendorExcel({
        filename: `Upcoming_Bills_${dayjs().format("YYYY-MM-DD")}`,
        reportTitle: "Payment Schedule — Bills (by vendor)",
        dateRangeLabel: dateLabel,
        vendorFilterLabel: vendorLabel,
        vendorSections: vendorGroups.map((g) => ({
          vendor_name: g.vendor_name,
          rows: g.bills,
          totalAmount: g.totalAmount,
        })),
        headers: billExportHeaders,
        mapRowToCells: mapBillToExportCells,
      });
      message.success("Excel exported: one sheet with a table per vendor and grand total.");
    } catch (e) {
      console.error(e);
      message.error(e?.message || "Excel export failed");
    }
  };

  const priorityFilteredData = useMemo(() => {
    return filteredBillsData.filter((b) => priorityItems.has(String(b.id)) || b.is_priority);
  }, [filteredBillsData, priorityItems]);

  const handleExportPriorityCSV = () => {
    if (priorityFilteredData.length === 0) {
      message.warning("No priority items to export");
      return;
    }

    const dateLabel = dateRange ? `${dateRange[0]?.format("YYYY-MM-DD")} to ${dateRange[1]?.format("YYYY-MM-DD")}` : "";
    const vendorLabel = selectedVendors.length > 0 ? selectedVendors.join(", ") : "All Vendors";
    const rows = [];

    const groups = {};
    priorityFilteredData.forEach((bill) => {
      const vName = bill.vendor_name || "Unknown";
      if (!groups[vName]) groups[vName] = { vendor_name: vName, bills: [], totalAmount: 0 };
      groups[vName].bills.push(bill);
      groups[vName].totalAmount += convertToUSD(bill.amount || 0, bill.currency || "$");
    });
    const priorityGroups = Object.values(groups).sort((a, b) => a.vendor_name.localeCompare(b.vendor_name));

    priorityGroups.forEach((group) => {
      rows.push([`=== Vendor: ${group.vendor_name} (priority) ===`, "", "", "", "", "", "", "", "", ""]);
      rows.push(billExportHeaders);
      group.bills.forEach((bill) => {
        rows.push([
          bill.vendor_name,
          bill.client_name || "",
          bill.brand_name || "",
          bill.invoice_date ? dayjs(bill.invoice_date).format("DD-MM-YYYY") : "",
          bill.invoice_number,
          bill.payment_terms_text || bill.description || "",
          bill.percentage ?? "",
          bill.due_date ? dayjs(bill.due_date).format("DD-MM-YYYY") : "",
          bill.due_in_text || "",
          convertToUSD(bill.amount || 0, bill.currency || "$").toFixed(2),
        ]);
      });
      rows.push(["Total", "", "", "", "", "", "", "", "", group.totalAmount.toFixed(2)]);
      rows.push([]);
    });

    const csvContent = [
      `Priority Payment Schedule - Bills (by vendor),,,,,,,,,`,
      `Date Range: ${dateLabel},,,,,,,,,`,
      `Vendors: ${vendorLabel},,,,,,,,,`,
      ``,
      ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Priority_Upcoming_Bills_${dayjs().format("YYYY-MM-DD")}.csv`;
    link.click();
    message.success("Priority items exported successfully!");
  };

  const handleExportPriorityExcel = async () => {
    if (priorityFilteredData.length === 0) {
      message.warning("No priority items to export");
      return;
    }
    const groups = {};
    priorityFilteredData.forEach((bill) => {
      const vName = bill.vendor_name || "Unknown";
      if (!groups[vName]) groups[vName] = { vendor_name: vName, bills: [], totalAmount: 0 };
      groups[vName].bills.push(bill);
      groups[vName].totalAmount += convertToUSD(bill.amount || 0, bill.currency || "$");
    });
    const priorityGroups = Object.values(groups).sort((a, b) => a.vendor_name.localeCompare(b.vendor_name));
    const dateLabel = dateRange
      ? `${dateRange[0]?.format("YYYY-MM-DD")} to ${dateRange[1]?.format("YYYY-MM-DD")}`
      : "";
    const vendorLabel = selectedVendors.length > 0 ? selectedVendors.join(", ") : "All Vendors";
    try {
      await exportUpcomingVendorExcel({
        filename: `Priority_Upcoming_Bills_${dayjs().format("YYYY-MM-DD")}`,
        reportTitle: "Priority — Bills (by vendor)",
        dateRangeLabel: dateLabel,
        vendorFilterLabel: vendorLabel,
        vendorSections: priorityGroups.map((g) => ({
          vendor_name: g.vendor_name,
          rows: g.bills,
          totalAmount: g.totalAmount,
        })),
        headers: billExportHeaders,
        mapRowToCells: mapBillToExportCells,
      });
      message.success("Priority Excel exported.");
    } catch (e) {
      console.error(e);
      message.error(e?.message || "Excel export failed");
    }
  };

  const collapseItems = useMemo(
    () =>
      vendorGroups.map((group) => {
        const gridHeight = Math.min(Math.max(group.bills.length * 40 + 100, 200), 600);
        return {
          key: group.vendor_name,
          label: (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Text strong style={{ fontSize: 14 }}>{group.vendor_name}</Text>
              <Space size="large">
                <Tag>{group.bills.length} bill{group.bills.length !== 1 ? "s" : ""}</Tag>
                <Text strong style={{ fontSize: 14 }}>
                  {currencyPrefix} {fmtAmount(group.totalAmount)}
                </Text>
              </Space>
            </div>
          ),
          children: (
            <div className="ag-theme-alpine" style={{ height: gridHeight, width: "100%" }}>
              <AgGridReact
                rowData={group.bills}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={(params) => String(params.data.id)}
                getRowStyle={getRowStyle}
                animateRows={false}
                suppressScrollOnNewData={true}
                domLayout="normal"
                headerHeight={36}
                rowHeight={40}
                enableCellTextSelection={true}
                ensureDomOrder={true}
                pinnedBottomRowData={[
                  {
                    id: `total-${group.vendor_name}`,
                    client_name: "Total",
                    brand_name: "",
                    invoice_date: null,
                    invoice_number: "",
                    payment_terms_text: "",
                    percentage: "",
                    due_date: null,
                    due_in_text: "",
                    amount: group.totalAmount,
                    currency: "$",
                    _isTotal: true,
                  },
                ]}
              />
            </div>
          ),
        };
      }),
    [vendorGroups, columnDefs, defaultColDef, getRowStyle, currencyPrefix, fmtAmount]
  );

  return (
    <Card className="upcoming-invoices-card">
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <Title level={4} style={{ margin: 0 }}>Payment Schedule — Bills</Title>
          <Space wrap>
            <Text strong>Vendor:</Text>
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="All Vendors"
              value={selectedVendors}
              onChange={(val) => setSelectedVendors(val || [])}
              options={vendorOptions}
              style={{ minWidth: 250, maxWidth: 500 }}
              maxTagCount="responsive"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
            <Text strong>Date Range:</Text>
            <RangePicker
              value={dateRange}
              onChange={(dates) =>
                setDateRange(dates || [dayjs().subtract(1, "month").startOf("month"), dayjs().endOf("month")])
              }
              allowClear={false}
            />
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              disabled={filteredBillsData.length === 0}
            >
              Export Excel
            </Button>
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExportPriorityExcel}
              disabled={priorityFilteredData.length === 0}
              style={priorityFilteredData.length > 0 ? { borderColor: "#faad14", color: "#faad14" } : {}}
            >
              Priority Excel ({priorityFilteredData.length})
            </Button>
          </Space>
        </div>

        <Row gutter={16}>
          <Col span={12}>
            <Card size="small">
              <Statistic title="Total Bills" value={totals.count} valueStyle={{ color: "#1890ff" }} />
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small">
              <Statistic
                title="Total Amount"
                value={isConverted ? totals.totalAmount * exchangeRate : totals.totalAmount}
                prefix={
                  isConverted ? (
                    <Tooltip title={`Original: $${totals.totalAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })} USD`}>
                      <span><SwapOutlined style={{ fontSize: 14, marginRight: 4 }} />{displayCurrency === "USD" ? "$" : ""}</span>
                    </Tooltip>
                  ) : "$"
                }
                suffix={isConverted ? displayCurrency : ""}
                precision={2}
                valueStyle={{ color: "#ff4d4f" }}
                formatter={(value) => value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              />
            </Card>
          </Col>
        </Row>

        {error && <Alert message={error} type="error" showIcon />}

        <Spin spinning={loading}>
          {vendorGroups.length === 0 && !loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
              No upcoming bills found for the selected date range.
            </div>
          ) : (
            <Collapse
              activeKey={activeKeys}
              onChange={(keys) => setActiveKeys(keys)}
              items={collapseItems}
              style={{ background: "#fff" }}
            />
          )}
        </Spin>
      </Space>
    </Card>
  );
};

export default UpcomingBills;
